import QuestionLayout from "./QuestionLayout";
import { useState } from "react";

function normalizePitchNameInput(input, allowAccidentals = true) {
  if (!input) return "";

  const s = input.trim();

  if (!allowAccidentals) {
    const m = s.match(/^([A-Ga-g])$/);
    return m ? m[1].toUpperCase() : s;
  }

  const m = s.match(/^([A-Ga-g])([#b♯♭]{0,2})$/);
  if (!m) return s;

  return m[1].toLowerCase() + m[2];
}

function insertAccidental(value, accGlyph) {
  return value + accGlyph;
}

function AccidentalButtons({ onSelect }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[
        { label: "♭", value: "♭" },
        { label: "♯", value: "♯" }
      ].map(({ label, value }) => (
        <button
          key={value}
          type="button"
          tabIndex={-1}
          style={{
            fontSize: 16,
            padding: "2px 6px",
            cursor: "pointer"
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(value);
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function RelMmWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const [focusedPart, setFocusedPart] = useState(null);

  const allowAccidentals = config.allowAccidentals !== false;
  const givenStart = config.givenStart ?? config.given ?? null;
  const givenEnd = config.givenEnd ?? null;

  function updatePart(id, nextValue) {
    onChange({
      ...value,
      [id]: normalizePitchNameInput(nextValue, allowAccidentals)
    });
  }

  function partFeedback(id) {
    return feedback?.parts?.[id] ?? null;
  }

  function inputBorder(id) {
    const fb = partFeedback(id);

    if (mode === "review") {
      return fb?.correct ? "2px solid #2e7d32" : "2px solid #c62828";
    }

    return "1px solid #aaa";
  }

  function SequenceGiven({ children }) {
    return (
      <span
        style={{
          fontSize: 18,
          fontWeight: "bold",
          minWidth: 42,
          textAlign: "center"
        }}
      >
        {children}
      </span>
    );
  }

  function SequenceInput({ part }) {
    return (
      <input
        key={part.id}
        type="text"
        value={value[part.id] || ""}
        disabled={mode !== "edit"}
        onChange={(e) => updatePart(part.id, e.target.value)}
        style={{
          fontSize: 16,
          textAlign: "center",
          padding: "8px 10px",
          borderRadius: 6,
          border: inputBorder(part.id),
          width: 50
        }}
      />
    );
  }

  if (config.displayMode === "sequence") {
    return (
      <QuestionLayout
        number={config.number}
        prompt={config.prompt}
        points={config.points}
        mode={mode}
        feedback={feedback}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10
          }}
        >
          {givenStart && <SequenceGiven>{givenStart}</SequenceGiven>}

          {(config.parts || []).map((part) => {
            const fb = partFeedback(part.id);

            return (
              <input
                key={part.id}
                type="text"
                value={value[part.id] || ""}
                disabled={mode !== "edit"}
                onChange={(e) => updatePart(part.id, e.target.value)}
                style={{
                  fontSize: 16,
                  textAlign: "center",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border:
                    mode === "review"
                      ? fb?.correct
                        ? "2px solid #2e7d32"
                        : "2px solid #c62828"
                      : "1px solid #aaa",
                  width: 50
                }}
              />
            );
          })}

          {givenEnd && <SequenceGiven>{givenEnd}</SequenceGiven>}
        </div>
      </QuestionLayout>
    );
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          // The accidental picker is absolutely positioned below the input, so
          // it does not add to the row's natural height. QuestionLayout's
          // horizontally scrollable content area also clips vertical overflow.
          // Reserve enough space for the picker instead of letting it get cut off.
          paddingBottom: allowAccidentals && mode === "edit" ? 36 : 0
        }}
      >
        {(config.parts || []).map((part) => (
          <div
            key={part.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >
            <span
              style={{
                fontSize: 18,
                fontWeight: "bold",
                minWidth: 42,
                textAlign: "right"
              }}
            >
              {part.given}
            </span>

            <span style={{ fontSize: 18, fontWeight: "bold" }}>/</span>

            <div style={{ position: "relative" }}>
              <input
                type="text"
                value={value[part.id] || ""}
                disabled={mode !== "edit"}
                onFocus={() => setFocusedPart(part.id)}
                onBlur={() => setFocusedPart(null)}
                onChange={(e) => updatePart(part.id, e.target.value)}
                style={{
                  fontSize: 16,
                  textAlign: "center",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: inputBorder(part.id),
                  width: 50
                }}
              />

              {allowAccidentals &&
                focusedPart === part.id &&
                mode === "edit" && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      marginTop: 4,
                      zIndex: 10
                    }}
                  >
                    <AccidentalButtons
                      onSelect={(acc) =>
                        updatePart(
                          part.id,
                          insertAccidental(value[part.id] || "", acc)
                        )
                      }
                    />
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>
    </QuestionLayout>
  );
}